import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  InteractableBox,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Icon
} from '@chakra-ui/react';
import { FaShippingFast, FaDownload, FaEye, FaTimes, FaMapMarkerAlt, FaBuilding, FaPhone } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

import { getAuthHeaders } from '../../utils/apiAuth';
const FedExLabelGenerator = ({ submissionId, onLabelGenerated, isOpen, onClose }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [generatedLabel, setGeneratedLabel] = useState(null);
  const [phlebotomistInfo, setPhlebotomistInfo] = useState(null);
  const [labInfo, setLabInfo] = useState(null);
  const [formData, setFormData] = useState({
    // Recipient information
    recipientName: '',
    recipientCompany: '',
    recipientAddress1: '',
    recipientAddress2: '',
    recipientCity: '',
    recipientState: '',
    recipientPostalCode: '',
    recipientCountryCode: 'US',
    recipientPhone: '',
    
    // Sender information (pre-filled from phlebotomist profile)
    senderName: '',
    senderCompany: '',
    senderAddress1: '',
    senderAddress2: '',
    senderCity: '',
    senderState: '',
    senderPostalCode: '',
    senderCountryCode: 'US',
    senderPhone: '',
    
    // Package details
    weight: 1,
    length: 10,
    width: 8,
    height: 6,
    
    // Shipping options
    serviceType: 'FEDEX_2_DAY',
    packagingType: 'YOUR_PACKAGING',
    
    // Special instructions
    specialInstructions: ''
  });

  const serviceTypes = [
    { value: 'FEDEX_2_DAY', label: 'FedEx 2Day' },
    { value: 'FEDEX_2_DAY_AM', label: 'FedEx 2Day A.M.' },
    { value: 'FEDEX_EXPRESS_SAVER', label: 'FedEx Express Saver' },
    { value: 'FEDEX_GROUND', label: 'FedEx Ground' },
    { value: 'FEDEX_HOME_DELIVERY', label: 'FedEx Home Delivery' },
    { value: 'FEDEX_STANDARD_OVERNIGHT', label: 'FedEx Standard Overnight' },
    { value: 'FEDEX_PRIORITY_OVERNIGHT', label: 'FedEx Priority Overnight' },
    { value: 'FEDEX_FIRST_OVERNIGHT', label: 'FedEx First Overnight' }
  ];

  const packagingTypes = [
    { value: 'YOUR_PACKAGING', label: 'Your Packaging' },
    { value: 'FEDEX_BOX', label: 'FedEx Box' },
    { value: 'FEDEX_PAK', label: 'FedEx Pak' },
    { value: 'FEDEX_TUBE', label: 'FedEx Tube' },
    { value: 'FEDEX_ENVELOPE', label: 'FedEx Envelope' }
  ];

  // Fetch phlebotomist and lab information when modal opens
  useEffect(() => {
    if (isOpen && submissionId) {
      fetchShippingInfo();
    }
  }, [isOpen, submissionId]);

  const fetchShippingInfo = async () => {
    try {
      // First get the submission to find the phlebotomist_id
      const { data: submission, error: submissionError } = await supabase
        .from('submissions')
        .select('phlebotomist_id')
        .eq('id', submissionId)
        .single();


      if (submissionError) {
        console.error('Error fetching submission:', submissionError);
        return;
      }

      if (submission?.phlebotomist_id) {
        // Fetch phlebotomist profile information
        const { data: phlebotomist, error: phlebotomistError } = await supabase
          .from('phlebotomist_profiles')
          .select('company_name, company_address, full_name, phone, saved_addresses')
          .eq('id', submission.phlebotomist_id)
          .single();

        if (phlebotomistError) {
          console.error('Error fetching phlebotomist info:', phlebotomistError);
          return;
        }

        if (phlebotomist) {
          setPhlebotomistInfo(phlebotomist);
          
          // Parse the company address and pre-fill sender information
          if (phlebotomist.company_address) {
            const addressParts = parseAddress(phlebotomist.company_address);
            setFormData(prev => ({
              ...prev,
              // Pre-fill sender information from phlebotomist profile
              senderName: phlebotomist.full_name || phlebotomist.company_name || '',
              senderCompany: phlebotomist.company_name || '',
              senderAddress1: addressParts.street || '',
              senderCity: addressParts.city || '',
              senderState: addressParts.state || '',
              senderPostalCode: addressParts.zip || '',
              senderPhone: phlebotomist.phone || ''
            }));
          }
        }
      }

      // Now fetch the lab information for the recipient
      const { data: submissionWithLab, error: labSubError } = await supabase
        .from('submissions')
        .select('lab_brand')
        .eq('id', submissionId)
        .single();

      if (!labSubError && submissionWithLab?.lab_brand) {
        // Get the lab details
        const { data: lab, error: labError } = await supabase
          .from('labs')
          .select('name, address, contact_phone, contact_email, city, state, zip_code, street_address')
          .eq('name', submissionWithLab.lab_brand)
          .single();

        if (!labError && lab) {
          setLabInfo(lab);

          // Pre-fill recipient information from lab
          setFormData(prev => ({
            ...prev,
            recipientName: lab.name || '',
            recipientCompany: lab.name || '',
            recipientAddress1: lab.street_address || lab.address || '',
            recipientCity: lab.city || '',
            recipientState: lab.state || '',
            recipientPostalCode: lab.zip_code || '',
            recipientPhone: lab.contact_phone || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching shipping information:', error);
    }
  };

  // Helper function to parse address string into components
  const parseAddress = (address) => {
    if (!address) return {};
    
    // Try to parse common address formats
    // Format: "123 Main St, City, State ZIP"
    const parts = address.split(',').map(part => part.trim());
    
    if (parts.length >= 3) {
      const lastPart = parts[parts.length - 1]; // "State ZIP"
      const stateZipMatch = lastPart.match(/([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/);
      
      return {
        street: parts[0],
        city: parts[1],
        state: stateZipMatch ? stateZipMatch[1] : '',
        zip: stateZipMatch ? stateZipMatch[2] : ''
      };
    }
    
    // Fallback: return the whole address as street
    return { street: address };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const required = [
      'recipientName', 'recipientAddress1', 'recipientCity', 'recipientState', 'recipientPostalCode',
      'senderName', 'senderAddress1', 'senderCity', 'senderState', 'senderPostalCode'
    ];
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      toast({
        title: 'Missing required fields',
        description: `Please fill in: ${missing.join(', ')}`,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      return false;
    }
    return true;
  };

  const handleGenerateLabel = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await fetch('/api/create-fedex-label', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          submissionId,
          recipient: {
            name: formData.recipientName,
            company: formData.recipientCompany,
            address1: formData.recipientAddress1,
            address2: formData.recipientAddress2,
            city: formData.recipientCity,
            state: formData.recipientState,
            postalCode: formData.recipientPostalCode,
            countryCode: formData.recipientCountryCode,
            phone: formData.recipientPhone
          },
          sender: {
            name: formData.senderName,
            company: formData.senderCompany,
            address1: formData.senderAddress1,
            address2: formData.senderAddress2,
            city: formData.senderCity,
            state: formData.senderState,
            postalCode: formData.senderPostalCode,
            countryCode: formData.senderCountryCode,
            phone: formData.senderPhone
          },
          packageDetails: {
            weight: formData.weight,
            length: formData.length,
            width: formData.width,
            height: formData.height
          },
          serviceType: formData.serviceType,
          packagingType: formData.packagingType
        })
      });

      const result = await response.json();

      if (result.success) {
        setGeneratedLabel(result.label);
        toast({
          title: 'FedEx Label Generated',
          description: 'Shipping label created successfully',
          status: 'success',
          duration: 5000,
          isClosable: true
        });
        
        if (onLabelGenerated) {
          onLabelGenerated(result.label);
        }
      } else {
        throw new Error(result.error || 'Failed to generate label');
      }
    } catch (error) {
      console.error('Error generating FedEx label:', error);
      toast({
        title: 'Error generating label',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabel = () => {
    if (generatedLabel?.labelUrl) {
      window.open(generatedLabel.labelUrl, '_blank');
    }
  };

  const handlePrintLabel = () => {
    if (generatedLabel?.labelUrl) {
      const printWindow = window.open(generatedLabel.labelUrl, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleClose = () => {
    setGeneratedLabel(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxH="90vh" overflowY="auto" borderRadius="xl" boxShadow="2xl">
        <ModalHeader 
          bgGradient="linear(to-r, blue.500, purple.600)" 
          color="white"
          borderRadius="xl 0 0 0"
          py={6}
        >
          <HStack spacing={3}>
            <Icon as={FaShippingFast} boxSize={6} />
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">Generate FedEx Label</Text>
              <Text fontSize="sm" opacity={0.9}>Create shipping label with auto-filled addresses</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: "whiteAlpha.200" }} />
        
        <ModalBody bg="gray.50" p={6}>
          <VStack spacing={6} align="stretch">
            {/* Shipping Addresses - Side by Side */}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
              {/* Ship To: Lab Address */}
              <Card 
                borderWidth="1px" 
                borderColor={labInfo ? 'green.200' : 'gray.200'} 
                borderRadius="xl"
                boxShadow={labInfo ? '0 4px 12px rgba(72, 187, 120, 0.15)' : 'sm'}
                transition="all 0.3s"
                _hover={{ transform: 'translateY(-2px)', boxShadow: labInfo ? '0 8px 20px rgba(72, 187, 120, 0.2)' : 'md' }}
              >
                <CardHeader 
                  bgGradient={labInfo ? 'linear(to-r, green.400, green.500)' : 'linear(to-r, gray.100, gray.200)'}
                  color={labInfo ? 'white' : 'gray.700'}
                  borderRadius="xl 0 0 0"
                  pb={4}
                >
                  <HStack spacing={3}>
                    <Icon as={FaMapMarkerAlt} boxSize={5} />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="md" fontWeight="bold">Ship To</Text>
                      {labInfo && (
                        <Text fontSize="xs" opacity={0.9}>Destination Lab</Text>
                      )}
                    </VStack>
                    {labInfo && (
                      <Badge 
                        bg="whiteAlpha.300" 
                        color="white" 
                        px={2} 
                        py={1} 
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        ✓ Ready
                      </Badge>
                    )}
                  </HStack>
                </CardHeader>
                <CardBody bg="white" borderRadius="0 0 xl xl" p={5}>
                  {labInfo ? (
                    <VStack align="start" spacing={3}>
                      <HStack spacing={2}>
                        <Icon as={FaBuilding} color="green.500" boxSize={4} />
                        <Text fontWeight="bold" fontSize="md" color="gray.800">
                          {formData.recipientName}
                        </Text>
                      </HStack>
                      <Box pl={6} borderLeft="2px solid" borderColor="green.200">
                        <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                          {formData.recipientAddress1}
                          {formData.recipientAddress2 && (
                            <><br />{formData.recipientAddress2}</>
                          )}
                          <br />
                          <Text as="span" fontWeight="medium">
                            {formData.recipientCity}, {formData.recipientState} {formData.recipientPostalCode}
                          </Text>
                        </Text>
                        {formData.recipientPhone && (
                          <HStack mt={2} spacing={2}>
                            <Icon as={FaPhone} color="gray.400" fontSize="xs" />
                            <Text fontSize="xs" color="gray.600" fontFamily="mono">
                              {formData.recipientPhone}
                            </Text>
                          </HStack>
                        )}
                      </Box>
                    </VStack>
                  ) : (
                    <Alert status="warning" borderRadius="md" size="sm">
                      <AlertIcon />
                      <Text fontSize="xs">Lab information not found in database</Text>
                    </Alert>
                  )}
                </CardBody>
              </Card>

              {/* Ship From: Phlebotomist Address */}
              <Card 
                borderWidth="1px" 
                borderColor={phlebotomistInfo ? 'blue.200' : 'gray.200'} 
                borderRadius="xl"
                boxShadow={phlebotomistInfo ? '0 4px 12px rgba(66, 153, 225, 0.15)' : 'sm'}
                transition="all 0.3s"
                _hover={{ transform: 'translateY(-2px)', boxShadow: phlebotomistInfo ? '0 8px 20px rgba(66, 153, 225, 0.2)' : 'md' }}
              >
                <CardHeader 
                  bgGradient={phlebotomistInfo ? 'linear(to-r, blue.400, blue.500)' : 'linear(to-r, gray.100, gray.200)'}
                  color={phlebotomistInfo ? 'white' : 'gray.700'}
                  borderRadius="xl 0 0 0"
                  pb={4}
                >
                  <HStack spacing={3}>
                    <Icon as={FaShippingFast} boxSize={5} />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="md" fontWeight="bold">Ship From</Text>
                      {phlebotomistInfo && (
                        <Text fontSize="xs" opacity={0.9}>Phlebotomist</Text>
                      )}
                    </VStack>
                    {phlebotomistInfo && (
                      <Badge 
                        bg="whiteAlpha.300" 
                        color="white" 
                        px={2} 
                        py={1} 
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        ✓ Ready
                      </Badge>
                    )}
                  </HStack>
                </CardHeader>
                <CardBody bg="white" borderRadius="0 0 xl xl" p={5}>
                  {phlebotomistInfo?.saved_addresses && Array.isArray(phlebotomistInfo.saved_addresses) && phlebotomistInfo.saved_addresses.length > 0 && (
                    <FormControl mb={4}>
                      <FormLabel fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
                        Choose Address
                      </FormLabel>
                      <Select 
                        size="sm"
                        borderRadius="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: 'blue.400' }}
                        _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                        onChange={(e) => {
                          const idx = parseInt(e.target.value, 10);
                          if (isNaN(idx)) return;
                          const sel = phlebotomistInfo.saved_addresses[idx];
                          const parts = sel?.street ? { street: sel.street, city: sel.city, state: sel.state, zip: sel.zip } : parseAddress(sel.address);
                          setFormData(prev => ({
                            ...prev,
                            senderName: phlebotomistInfo.full_name || phlebotomistInfo.company_name || '',
                            senderCompany: phlebotomistInfo.company_name || '',
                            senderAddress1: parts.street || '',
                            senderCity: parts.city || '',
                            senderState: parts.state || '',
                            senderPostalCode: parts.zip || '',
                            senderPhone: sel.phone || phlebotomistInfo.phone || ''
                          }));
                        }}
                        placeholder="Select saved address"
                      >
                        {phlebotomistInfo.saved_addresses.map((a, i) => {
                          const summary = a.address || `${a.street || ''} ${a.city ? ', ' + a.city : ''} ${a.state || ''} ${a.zip || ''}`.trim();
                          return <option key={i} value={i}>{a.label || `Address ${i+1}`} — {summary}</option>;
                        })}
                      </Select>
                    </FormControl>
                  )}
                  {phlebotomistInfo ? (
                    <VStack align="start" spacing={3}>
                      <HStack spacing={2}>
                        <Icon as={FaBuilding} color="blue.500" boxSize={4} />
                        <Text fontWeight="bold" fontSize="md" color="gray.800">
                          {formData.senderCompany}
                        </Text>
                      </HStack>
                      <Box pl={6} borderLeft="2px solid" borderColor="blue.200">
                        <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">
                          {formData.senderName}
                        </Text>
                        <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                          {formData.senderAddress1}
                          {formData.senderAddress2 && (
                            <><br />{formData.senderAddress2}</>
                          )}
                          <br />
                          <Text as="span" fontWeight="medium">
                            {formData.senderCity}, {formData.senderState} {formData.senderPostalCode}
                          </Text>
                        </Text>
                        {formData.senderPhone && (
                          <HStack mt={2} spacing={2}>
                            <Icon as={FaPhone} color="gray.400" fontSize="xs" />
                            <Text fontSize="xs" color="gray.600" fontFamily="mono">
                              {formData.senderPhone}
                            </Text>
                          </HStack>
                        )}
                      </Box>
                    </VStack>
                  ) : (
                    <Alert status="warning" borderRadius="md" size="sm">
                      <AlertIcon />
                      <Text fontSize="xs">Mobile lab information not found in database</Text>
                    </Alert>
                  )}
                </CardBody>
              </Card>
            </Grid>

            {/* Package Details & Shipping Options - Side by Side */}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
              {/* Package Details */}
              <Card 
                borderWidth="1px" 
                borderColor="purple.200" 
                borderRadius="xl"
                boxShadow="0 4px 12px rgba(128, 90, 213, 0.15)"
                transition="all 0.3s"
                _hover={{ transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(128, 90, 213, 0.2)' }}
              >
                <CardHeader 
                  bgGradient="linear(to-r, purple.400, purple.500)"
                  color="white"
                  borderRadius="xl 0 0 0"
                  pb={4}
                >
                  <HStack spacing={3}>
                    <Icon as={FaShippingFast} boxSize={5} />
                    <Text fontSize="md" fontWeight="bold">Package Details</Text>
                  </HStack>
                </CardHeader>
                <CardBody bg="white" borderRadius="0 0 xl xl" p={5}>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
                          Weight (lbs)
                        </FormLabel>
                        <NumberInput
                          value={formData.weight}
                          onChange={(value) => handleInputChange('weight', parseFloat(value) || 0)}
                          min={0.1}
                          max={150}
                          precision={1}
                          size="md"
                          borderColor="gray.300"
                          borderRadius="md"
                          _hover={{ borderColor: 'purple.400' }}
                          _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px purple.500' }}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
                          Length (in)
                        </FormLabel>
                        <NumberInput
                          value={formData.length}
                          onChange={(value) => handleInputChange('length', parseFloat(value) || 0)}
                          min={1}
                          max={108}
                          precision={0.1}
                          size="md"
                          borderColor="gray.300"
                          borderRadius="md"
                          _hover={{ borderColor: 'purple.400' }}
                          _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px purple.500' }}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
                          Width (in)
                        </FormLabel>
                        <NumberInput
                          value={formData.width}
                          onChange={(value) => handleInputChange('width', parseFloat(value) || 0)}
                          min={1}
                          max={108}
                          precision={0.1}
                          size="md"
                          borderColor="gray.300"
                          borderRadius="md"
                          _hover={{ borderColor: 'purple.400' }}
                          _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px purple.500' }}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
                          Height (in)
                        </FormLabel>
                        <NumberInput
                          value={formData.height}
                          onChange={(value) => handleInputChange('height', parseFloat(value) || 0)}
                          min={1}
                          max={108}
                          precision={0.1}
                          size="md"
                          borderColor="gray.300"
                          borderRadius="md"
                          _hover={{ borderColor: 'purple.400' }}
                          _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px purple.500' }}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </GridItem>
                  </Grid>
                </CardBody>
              </Card>

              {/* Shipping Options */}
              <Card 
                borderWidth="1px" 
                borderColor="orange.200" 
                borderRadius="xl"
                boxShadow="0 4px 12px rgba(237, 137, 54, 0.15)"
                transition="all 0.3s"
                _hover={{ transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(237, 137, 54, 0.2)' }}
              >
                <CardHeader 
                  bgGradient="linear(to-r, orange.400, orange.500)"
                  color="white"
                  borderRadius="xl 0 0 0"
                  pb={4}
                >
                  <HStack spacing={3}>
                    <Icon as={FaShippingFast} boxSize={5} />
                    <Text fontSize="md" fontWeight="bold">Shipping Options</Text>
                  </HStack>
                </CardHeader>
                <CardBody bg="white" borderRadius="0 0 xl xl" p={5}>
                  <VStack spacing={5} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
                        Service Type
                      </FormLabel>
                      <Select
                        value={formData.serviceType}
                        onChange={(e) => handleInputChange('serviceType', e.target.value)}
                        size="md"
                        borderRadius="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: 'orange.400' }}
                        _focus={{ borderColor: 'orange.500', boxShadow: '0 0 0 1px orange.500' }}
                      >
                        {serviceTypes.map(service => (
                          <option key={service.value} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
                        Packaging Type
                      </FormLabel>
                      <Select
                        value={formData.packagingType}
                        onChange={(e) => handleInputChange('packagingType', e.target.value)}
                        size="md"
                        borderRadius="md"
                        borderColor="gray.300"
                        _hover={{ borderColor: 'orange.400' }}
                        _focus={{ borderColor: 'orange.500', boxShadow: '0 0 0 1px orange.500' }}
                      >
                        {packagingTypes.map(packaging => (
                          <option key={packaging.value} value={packaging.value}>
                            {packaging.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </Grid>

            {/* Generated Label Display */}
            {generatedLabel && (
              <Card 
                borderWidth="2px" 
                borderColor="green.400" 
                bg="white"
                borderRadius="xl"
                boxShadow="0 8px 24px rgba(72, 187, 120, 0.25)"
                overflow="hidden"
              >
                <CardHeader 
                  bgGradient="linear(to-r, green.400, green.500)"
                  color="white"
                  borderRadius="xl 0 0 0"
                  pb={4}
                >
                  <HStack spacing={3}>
                    <Icon as={FaDownload} boxSize={5} />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="md" fontWeight="bold">Label Generated Successfully</Text>
                      <Text fontSize="xs" opacity={0.9}>Ready to download and print</Text>
                    </VStack>
                    <Badge 
                      bg="whiteAlpha.300" 
                      color="white" 
                      px={3} 
                      py={1} 
                      borderRadius="full"
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      ${generatedLabel.totalNetCharge?.amount || 'N/A'}
                    </Badge>
                  </HStack>
                </CardHeader>
                <CardBody bg="white" p={6}>
                  <VStack spacing={5} align="stretch">
                    <Box 
                      bg="green.50" 
                      p={4} 
                      borderRadius="lg" 
                      borderLeft="4px solid"
                      borderColor="green.500"
                    >
                      <Text fontSize="xs" color="gray.600" mb={2} fontWeight="medium">
                        Tracking Number
                      </Text>
                      <Text 
                        fontSize="xl" 
                        fontFamily="mono" 
                        fontWeight="bold" 
                        color="green.700"
                        letterSpacing="wide"
                      >
                        {generatedLabel.trackingNumber}
                      </Text>
                    </Box>
                    <HStack spacing={3}>
                      <Button
                        leftIcon={<FaDownload />}
                        colorScheme="blue"
                        onClick={handleDownloadLabel}
                        size="md"
                        flex={1}
                        borderRadius="lg"
                        fontWeight="semibold"
                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                        transition="all 0.2s"
                      >
                        Download Label
                      </Button>
                      <Button
                        leftIcon={<FaEye />}
                        colorScheme="green"
                        onClick={handlePrintLabel}
                        size="md"
                        flex={1}
                        borderRadius="lg"
                        fontWeight="semibold"
                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                        transition="all 0.2s"
                      >
                        Print Label
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter 
          bg="gray.50" 
          borderTop="1px solid"
          borderColor="gray.200"
          borderRadius="0 0 xl xl"
          py={4}
          px={6}
        >
          <HStack spacing={3} w="full" justify="flex-end">
            <Button 
              variant="ghost" 
              onClick={handleClose}
              borderRadius="lg"
              px={6}
              _hover={{ bg: 'gray.100' }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleGenerateLabel}
              isLoading={loading}
              loadingText="Generating..."
              leftIcon={<FaShippingFast />}
              size="lg"
              borderRadius="lg"
              px={8}
              bgGradient="linear(to-r, blue.500, blue.600)"
              _hover={{ 
                bgGradient: 'linear(to-r, blue.600, blue.700)',
                transform: 'translateY(-2px)',
                boxShadow: 'lg'
              }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.2s"
              fontWeight="semibold"
            >
              Generate Label
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FedExLabelGenerator;
